import React, { PureComponent } from 'react';

// Character art lives alongside the existing performance-feedback
// images (`pf1.png`, `pf2.png`). Save the JPGs at these paths to
// match the imports below.
import leaderImg from '../../containers/assets/market-leader.jpg';
import challengerImg from '../../containers/assets/market-challenger.jpg';
import climberImg from '../../containers/assets/market-climber.jpg';
import warriorImg from '../../containers/assets/market-warrior.jpg';
import newbieImg from '../../containers/assets/market-newbie.jpg';

import { Styled } from './styled';

// Per-rank presentation. `celebrate` gates the confetti (only the
// top three ranks). `sub` receives the team name so the copy reads
// personal where it can.
const RANK_META = {
  1: {
    word: 'Market Leader',
    accent: '#D9A93E',
    image: leaderImg,
    celebrate: true,
    sub: () => "Congrats on finishing first! Let's keep it up!",
    confetti: ['#D9A93E', '#1F9D6B', '#E0533A', '#3E8FD8'],
  },
  2: {
    word: 'Market Challenger',
    accent: '#6E8CA8',
    image: challengerImg,
    celebrate: true,
    sub: () => "Second place, and right on the leader's heels.",
    confetti: ['#6E8CA8', '#A9B0B8', '#D9A93E'],
  },
  3: {
    word: 'Market Climber',
    accent: '#2E9E8F',
    image: climberImg,
    celebrate: true,
    sub: () => "Third place and still climbing. The summit is in sight.",
    confetti: ['#2E9E8F', '#7FC8A0', '#D9A93E'],
  },
  4: {
    word: 'Market Warrior',
    accent: '#B23A48',
    image: warriorImg,
    celebrate: false,
    sub: () => 'Fourth place, earned the hard way. You fought for every point.',
    confetti: [],
  },
  5: {
    word: 'Market Newbie',
    accent: '#7FB2D9',
    image: newbieImg,
    celebrate: false,
    sub: () => "Rookie numbers. Grow up fast, the next round won't wait for you.",
    confetti: [],
  },
};

// Team names in this product are often stored as a bare number
// ("4", "12"). Prefix with "Team " unless the upstream name already
// starts with that word so we don't double-prefix things like
// "Team Northstar".
const formatTeamName = (name) => {
  const str = (name == null ? '' : String(name)).trim();
  if (!str) return 'Your team';
  return /^team\s/i.test(str) ? str : `Team ${str}`;
};

// Backend `ranking` is a string ("Market Leader" etc.). Mapped to
// the 1-5 tier the design works in.
export const RANK_NAME_TO_NUMBER = {
  'market leader': 1,
  'market challenger': 2,
  'market climber': 3,
  'market warrior': 4,
  'market newbie': 5,
};

// Scores are stored as fractions (0..1). Display as percentage with
// two decimals to match how step5 / Final Scores format the same
// underlying number.
const fmtPct = (n) => {
  if (n == null || isNaN(Number(n))) return '—';
  return `${(Number(n) * 100).toFixed(2)}%`;
};

// Confetti pieces. Generated once on construction so the random
// timings don't reshuffle across re-renders (React 15 has no
// useMemo — instance fields are the equivalent here).
class Confetti extends PureComponent {
  constructor(props) {
    super(props);
    const colors = props.colors || [];
    this.pieces = [];
    for (let k = 0; k < 12; k += 1) {
      this.pieces.push({
        left: `${4 + k * 8}%`,
        background: colors[k % Math.max(colors.length, 1)] || '#D9A93E',
        animationDuration: `${(5 + Math.random() * 2.5).toFixed(2)}s`,
        animationDelay: `${(Math.random() * 4).toFixed(2)}s`,
      });
    }
  }

  render() {
    return (
      <Styled.Confetti aria-hidden="true">
        {this.pieces.map((p, i) => (
          <i key={i} style={p} />
        ))}
      </Styled.Confetti>
    );
  }
}

class MarketRanking extends PureComponent {
  // The parent PerformanceFeedback layout (Content + ContentWrapper)
  // paints a #f5f6f7 gray background that shows through to the
  // right of the 794px max-width and in the gap above the Pagination
  // bar. For this page only we want a single white panel — so on
  // mount, walk up the DOM, swap any matching gray ancestors to
  // white, and restore the originals on unmount so other steps
  // keep their gray.
  componentDidMount() {
    if (typeof window === 'undefined' || !this._rootEl) return;
    this._modifiedNodes = [];
    let node = this._rootEl.parentElement;
    let hops = 0;
    while (node && hops < 6) {
      const cs = window.getComputedStyle(node);
      if (cs.backgroundColor === 'rgb(245, 246, 247)') {
        this._modifiedNodes.push({
          el: node,
          originalBg: node.style.background,
        });
        node.style.background = '#ffffff';
      }
      node = node.parentElement;
      hops += 1;
    }
  }

  componentWillUnmount() {
    if (this._modifiedNodes) {
      this._modifiedNodes.forEach((entry) => {
        entry.el.style.background = entry.originalBg;
      });
      this._modifiedNodes = null;
    }
  }

  setRootEl = (el) => {
    this._rootEl = el;
  };

  render() {
    const {
      teamName,
      rank,
      totalTeams,
      score,
      changePct,
      cohort,
    } = this.props;

    const meta = RANK_META[rank];
    if (!meta) {
      // Defensive: don't crash on an unknown rank. The parent should
      // gate on a recognized rank before rendering, but if something
      // slips through, show a friendly placeholder instead of nothing.
      return (
        <Styled.Root innerRef={this.setRootEl}>
          <Styled.Content>
            <Styled.Empty>
              <h2>Round results unavailable</h2>
              <p>We couldn&apos;t determine your ranking for this round yet.</p>
            </Styled.Empty>
          </Styled.Content>
        </Styled.Root>
      );
    }

    const hasChange = changePct != null && !isNaN(Number(changePct));
    const up = hasChange ? Number(changePct) >= 0 : true;
    const displayTeamName = formatTeamName(teamName);

    return (
      <Styled.Root innerRef={this.setRootEl} style={{ '--mr-accent': meta.accent }}>
        {meta.celebrate && <Confetti colors={meta.confetti} />}

        <Styled.Content>
          <Styled.Eyebrow>Round Performance</Styled.Eyebrow>
          <Styled.Title>{meta.word}</Styled.Title>

          <Styled.PortraitWrap>
            <Styled.Portrait>
              <img src={meta.image} alt={`${meta.word} mascot`} />
            </Styled.Portrait>
          </Styled.PortraitWrap>

          <Styled.Team>
            <strong>{displayTeamName}</strong> &middot; Rank {rank} of {totalTeams}
          </Styled.Team>
          <Styled.Sub>{meta.sub(displayTeamName)}</Styled.Sub>

          <Styled.Card>
            <Styled.ScoreTop>
              <div>
                <Styled.ScoreLabel>Team score</Styled.ScoreLabel>
                <Styled.ScoreVal>{fmtPct(score)}</Styled.ScoreVal>
              </div>
              {hasChange && (
                <Styled.Pill up={up}>
                  {up ? '▲' : '▼'} {Math.abs(Number(changePct)).toFixed(1)}%
                </Styled.Pill>
              )}
            </Styled.ScoreTop>

            {cohort && (
              <Styled.Grid>
                <div>
                  <Styled.GridLabel>Highest</Styled.GridLabel>
                  <Styled.GridVal>{fmtPct(cohort.highest)}</Styled.GridVal>
                </div>
                <div>
                  <Styled.GridLabel>Median</Styled.GridLabel>
                  <Styled.GridVal>{fmtPct(cohort.median)}</Styled.GridVal>
                </div>
                <div>
                  <Styled.GridLabel>Average</Styled.GridLabel>
                  <Styled.GridVal>{fmtPct(cohort.average)}</Styled.GridVal>
                </div>
              </Styled.Grid>
            )}
          </Styled.Card>
        </Styled.Content>
      </Styled.Root>
    );
  }
}

export default MarketRanking;



// WEBPACK FOOTER //
// ./src/components/market-ranking/index.js